"use client";

/**
 * ElasticSlider — the canonical single-value slider primitive for `@aisquare/ui`.
 *
 * A faithful reimplementation of the React Bits "Elastic Slider" interaction
 * (NOT a Radix slider). The track is a custom horizontal bar flanked by a left
 * and a right icon (volume down / up by default) with a numeric readout. The
 * feel is the point: while dragging, pulling the pointer PAST either edge of the
 * track makes the whole bar STRETCH elastically outward — `scaleX` grows while
 * `scaleY` thins, the transform-origin flips to the opposite edge so it grows
 * away from the pointer, and the overshot end-icon nudges out and pulses. The
 * overshoot saturates (rubber-bands) toward a cap via a sigmoid decay, and on
 * release the bar SPRINGS back with a bouncy spring. Hovering / touching the
 * bar swells it slightly (scale 1 -> 1.2, height 6px -> 12px).
 *
 * Conventions vs the reference: brand token classes (bg-muted track, bg-primary
 * fill, text-muted-foreground icons/readout) instead of bundled CSS, lucide
 * `Volume1`/`Volume2` icons instead of Chakra/react-icons, and two things the
 * reference lacks entirely — (1) full keyboard + ARIA `role="slider"` support,
 * and (2) `prefers-reduced-motion` safety: when reduced, the elastic overshoot,
 * the spring-back, and the hover swell are all suppressed while the bar stays
 * fully draggable and reports accurate values.
 *
 * The public API is single-value (a scalar `value` / `onValueChange`), keeping
 * our existing contract. Controlled (`value`) takes precedence over the internal
 * uncontrolled state seeded from `defaultValue`.
 */
import * as React from "react";
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "motion/react";
import { Volume1, Volume2 } from "lucide-react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/** Max pixels the track may be dragged past an edge before the stretch saturates. */
const MAX_OVERFLOW = 50;
/** Bouncy spring used to snap the over-stretched track back on release. */
const SPRINGBACK = { type: "spring", bounce: 0.5 } as const;

type EdgeRegion = "left" | "middle" | "right";

/**
 * Sigmoid rubber-band decay. Maps a raw over-drag distance to a saturating
 * overshoot bounded by `max`, so dragging further past the edge yields ever
 * smaller gains (the elastic "give"). Pure math, re-derived (not copied):
 *   entry = value / max
 *   s     = 2 * (1 / (1 + e^-entry) - 0.5)   // logistic, recentred to 0
 *   out   = s * max
 */
function decay(value: number, max: number): number {
  if (max === 0) return 0;
  const entry = value / max;
  const sigmoid = 2 * (1 / (1 + Math.exp(-entry)) - 0.5);
  return sigmoid * max;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export interface ElasticSliderProps
  extends Omit<
    React.ComponentPropsWithoutRef<"div">,
    "defaultValue" | "onChange" | "children"
  > {
  /** Controlled value (single thumb). Takes precedence over `defaultValue`. */
  value?: number;
  /** Uncontrolled initial value. */
  defaultValue?: number;
  /** Minimum value. */
  min?: number;
  /** Maximum value. */
  max?: number;
  /** Step granularity. When set, values snap to the nearest multiple of `step`. */
  step?: number;
  /** Fires on every value change (drag move + keyboard step). */
  onValueChange?: (value: number) => void;
  /** Fires once the interaction settles (pointer up / keyboard). */
  onValueCommit?: (value: number) => void;
  /** Leading icon (defaults to a low-volume glyph). */
  leftIcon?: React.ReactNode;
  /** Trailing icon (defaults to a high-volume glyph). */
  rightIcon?: React.ReactNode;
  /** Show the numeric value readout to the right. Default true. */
  showValue?: boolean;
  /** Disable interaction. */
  disabled?: boolean;
}

export const ElasticSlider = React.forwardRef<HTMLDivElement, ElasticSliderProps>(
  function ElasticSlider(
    {
      className,
      value: controlledValue,
      defaultValue = 50,
      min = 0,
      max = 100,
      step,
      onValueChange,
      onValueCommit,
      leftIcon,
      rightIcon,
      showValue = true,
      disabled = false,
      ...props
    },
    ref,
  ) {
    const reduced = useReducedMotion();
    // `null` (pre-resolve) is treated as motion-allowed to avoid an SSR flash,
    // matching the hook's documented semantics.
    const motionAllowed = reduced !== true;

    const isStepped = step != null;
    const isControlled = controlledValue != null;

    const [internalValue, setInternalValue] = React.useState(() =>
      clamp(defaultValue, min, max),
    );
    // React Bits syncs internal state when `defaultValue` changes; we honour that
    // for the uncontrolled case only — a controlled `value` always wins.
    React.useEffect(() => {
      if (!isControlled) setInternalValue(clamp(defaultValue, min, max));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const displayValue = isControlled
      ? clamp(controlledValue as number, min, max)
      : internalValue;

    const sliderRef = React.useRef<HTMLDivElement>(null);

    // Motion values: the latest pointer X, the saturated over-drag, and the
    // hover/touch swell scale.
    const clientX = useMotionValue(0);
    const overflow = useMotionValue(0);
    const scale = useMotionValue(1);

    // Which edge (if any) the pointer is currently past. A ref because the
    // motion-value subscriber reads it without needing to re-render.
    const region = React.useRef<EdgeRegion>("middle");

    // Re-measure the over-drag whenever the pointer moves and translate it into a
    // saturated overflow. Suppressed entirely under reduced motion so the track
    // never stretches.
    useMotionValueEvent(clientX, "change", latest => {
      if (!motionAllowed) return;
      const rect = sliderRef.current?.getBoundingClientRect();
      if (!rect) return;
      let newRegion: EdgeRegion = "middle";
      let distance = 0;
      if (latest < rect.left) {
        newRegion = "left";
        distance = rect.left - latest;
      } else if (latest > rect.right) {
        newRegion = "right";
        distance = latest - rect.right;
      }
      region.current = newRegion;
      overflow.jump(decay(distance, MAX_OVERFLOW));
    });

    // Track wrapper transforms. Each reads the live overflow; the width is read
    // off the rect at transform time and guarded so the first paint (no rect)
    // returns sane defaults.
    const trackScaleX = useTransform(() => {
      const width = sliderRef.current?.getBoundingClientRect().width;
      if (!width) return 1;
      return 1 + overflow.get() / width;
    });
    const trackScaleY = useTransform(overflow, [0, MAX_OVERFLOW], [1, 0.8]);
    const trackTransformOrigin = useTransform(() =>
      region.current === "left" ? "right" : "left",
    );
    const trackHeight = useTransform(scale, [1, 1.2], [6, 12]);
    const trackMargin = useTransform(scale, [1, 1.2], [0, -3]);
    const wrapperOpacity = useTransform(scale, [1, 1.2], [0.7, 1]);

    // End-icon ride: each icon translates with the stretch on its own edge and
    // pulses when that edge is the active over-drag region.
    const leftIconX = useTransform(() =>
      region.current === "left" ? -overflow.get() / scale.get() : 0,
    );
    const rightIconX = useTransform(() =>
      region.current === "right" ? overflow.get() / scale.get() : 0,
    );

    const commit = (next: number) => {
      onValueChange?.(next);
      onValueCommit?.(next);
    };

    const setFromPointer = (eClientX: number) => {
      const rect = sliderRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const ratio = (eClientX - rect.left) / rect.width;
      let next = min + ratio * (max - min);
      if (isStepped) next = Math.round(next / (step as number)) * (step as number);
      next = clamp(next, min, max);
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
      clientX.jump(eClientX);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || e.buttons <= 0) return;
      setFromPointer(e.clientX);
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      setFromPointer(e.clientX);
      e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const handlePointerUp = () => {
      if (disabled) return;
      region.current = "middle";
      if (motionAllowed) animate(overflow, 0, SPRINGBACK);
      else overflow.jump(0);
      onValueCommit?.(displayValue);
    };

    const handleHoverStart = () => {
      if (disabled || !motionAllowed) return;
      animate(scale, 1.2);
    };
    const handleHoverEnd = () => {
      if (!motionAllowed) return;
      animate(scale, 1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      const stepBy = step ?? 1;
      const pageBy = Math.max(stepBy, (max - min) / 10);
      let next: number | null = null;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowUp":
          next = displayValue + stepBy;
          break;
        case "ArrowLeft":
        case "ArrowDown":
          next = displayValue - stepBy;
          break;
        case "PageUp":
          next = displayValue + pageBy;
          break;
        case "PageDown":
          next = displayValue - pageBy;
          break;
        case "Home":
          next = min;
          break;
        case "End":
          next = max;
          break;
        default:
          return;
      }
      e.preventDefault();
      next = clamp(next, min, max);
      if (!isControlled) setInternalValue(next);
      commit(next);
    };

    const fillPercent =
      max === min ? 0 : ((displayValue - min) / (max - min)) * 100;

    const left = leftIcon ?? <Volume1 className="size-5" aria-hidden="true" />;
    const right = rightIcon ?? <Volume2 className="size-5" aria-hidden="true" />;

    return (
      <div
        ref={ref}
        data-slot="elastic-slider"
        className={cn(
          "flex w-full flex-col items-center gap-3",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
        {...props}
      >
        <motion.div
          // The interactive bar: icons flank a stretchable track. Hover/touch
          // swell is driven here so the whole assembly grows together.
          onHoverStart={handleHoverStart}
          onHoverEnd={handleHoverEnd}
          onTouchStart={handleHoverStart}
          onTouchEnd={handleHoverEnd}
          style={{ opacity: motionAllowed ? wrapperOpacity : 1 }}
          className="flex w-full touch-none items-center justify-center gap-3 py-2 select-none"
        >
          <motion.div
            style={{ x: motionAllowed ? leftIconX : 0 }}
            animate={
              motionAllowed && region.current === "left"
                ? { scale: [1, 1.4, 1] }
                : { scale: 1 }
            }
            className="shrink-0 text-muted-foreground"
          >
            {left}
          </motion.div>

          <div
            ref={sliderRef}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={Math.round(displayValue)}
            aria-orientation="horizontal"
            aria-disabled={disabled || undefined}
            onPointerMove={handlePointerMove}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onLostPointerCapture={handlePointerUp}
            onKeyDown={handleKeyDown}
            className="relative flex grow cursor-grab touch-none items-center py-2 select-none focus-visible:outline-none active:cursor-grabbing"
          >
            <motion.div
              data-slot="elastic-slider-track"
              style={{
                scaleX: motionAllowed ? trackScaleX : 1,
                scaleY: motionAllowed ? trackScaleY : 1,
                height: motionAllowed ? trackHeight : 6,
                marginTop: motionAllowed ? trackMargin : 0,
                marginBottom: motionAllowed ? trackMargin : 0,
                transformOrigin: motionAllowed ? trackTransformOrigin : "left",
              }}
              className="relative w-full overflow-hidden rounded-full bg-muted"
            >
              <div
                data-slot="elastic-slider-range"
                className="absolute top-0 left-0 h-full rounded-full bg-primary"
                style={{ width: `${fillPercent}%` }}
              />
            </motion.div>
          </div>

          <motion.div
            style={{ x: motionAllowed ? rightIconX : 0 }}
            animate={
              motionAllowed && region.current === "right"
                ? { scale: [1, 1.4, 1] }
                : { scale: 1 }
            }
            className="shrink-0 text-muted-foreground"
          >
            {right}
          </motion.div>
        </motion.div>

        {showValue ? (
          <p
            data-slot="elastic-slider-value"
            className="text-sm text-muted-foreground tabular-nums"
          >
            {Math.round(displayValue)}
          </p>
        ) : null}
      </div>
    );
  },
);
ElasticSlider.displayName = "ElasticSlider";
